<template>
  <VForm id="form-classbook-notice"
         ref="form"
         @submit="createNotice"
  >
    <SModalPortalTo v-if="localNotice.date" is-modal-header-sub-title>
      <div class="d-flex align-items-center small justify-content-center mt-1">
        <time class="h4 font-weight-light m-0"
              data-cy="notice-date-time"
        >{{ $t('global.time', { time: $dateFormat.date_time(localNotice.date) }) }}
        </time>

        <!--      Only allow change date if it's creation-->
        <b-button
          v-if="!editMode"
          variant="transparent"
          class="primary p-0 smaller-btn ml-1 auto-height font-weight-light"
          data-cy="change-time"
          @click="modals.datetime = true"
        >
          {{ $t('global.change') }}
        </b-button>
      </div>
    </SModalPortalTo>

    <STextarea
      v-model="localNotice.noticeMessage"
      validation-rules="required"
      validation-field-name="notice_message"
      spellcheck="true"
      :label-text="$t('classbook.notices.create.input')"
      hide-label
      :placeholder="$t('classbook.notices.create.input')"
      :rows="4"
      no-resize
      :max-height="186"
      data-cy="notice-message"
    />

    <SList class="mt-4">
      <!--      Only allow add users if it's creation-->
      <SListItem
        primary-text-color
        icon="icon-add-o"
        :title="$t('classbook.notices.create.add_students')"
        data-cy="open-users-modal"
        @click="openUsersModal"
      />

      <sdui-user-list-item v-for="user in localNotice.localUsers"
                           :key="`notice-user-${user.id}`"
                           :user="user"
                           data-cy="notice-user-item"
      >
        <template #action>
          <b-button class="p-1 mr-n1"
                    variant="transparent"
                    data-cy="remove-user"
                    @click="removeUser(user.id)"
          >
            <span class="sr-only">{{ $t('classbook.notices.create.remove_students') }}</span>
            <sdui-icon class="icon-close-o-16 gray-300" />
          </b-button>
        </template>
      </sdui-user-list-item>
    </SList>

    <div class="categories mt-4">
      <h3 class="mb-2">{{ $t('classbook.notices.create.categories') }}</h3>
      <sdui-loader v-if="$wait.waiting('fetching-categories')" class="p-5" />

      <div class="categories-inner mb-4">
        <sdui-group-select :current-filter-type="localNotice.category"
                           :options="calculatedCategories"
                           @filter-type-changed="(data) => (localNotice.category = data)"
        />
      </div>
    </div>

    <SModalPortalTo>
      <div id="disabled-wrapper" class="w-100 next-button-wrapper">
        <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
        <b-button
          variant="primary"
          type="submit"
          form="form-classbook-notice"
          block
          size="lg"
          :disabled="$wait.waiting('creating-notice')"
          data-cy="save-notice"
        >
          {{ $t('global.save') }}
        </b-button>
      </div>
    </SModalPortalTo>

    <!--    date popover -->
    <SModal v-model="modals.datetime"
            :header-title="$t('classbook.notices.create.edit_date')"
            :show-footer-gradient="false"
    >
      <sdui-date-time-select v-if="modals.datetime"
                             v-model="localNotice.date"
                             class="mb-4"
      />

      <SModalPortalTo>
        <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
        <b-button
          type="submit"
          size="lg"
          block
          data-cy="save-time"
          variant="primary"
          :class="{ 'spinner-btn': $wait.waiting('creating-survey') }"
          :disabled="$wait.waiting('creating-survey')"
          @click="modals.datetime = false"
        >
          {{ $t('global.save') }}
        </b-button>
      </SModalPortalTo>
    </SModal>

    <!--    users modal -->
    <SModal
      v-model="modals.users"
      :show-footer-gradient="false"
      :header-title="$t('classbook.notices.create.choose_students_modal_title')"
      data-cy="notice-creation-select-users-modal"
      @hidden="handleNoticeUserSelectModalDisplay(true)"
    >
      <classbook-notice-users
        v-if="modals.users"
        v-model="selectedUsers"
        :entry="entry"
        @save-users="saveUsers"
        @close="handleNoticeUserSelectModalDisplay(true)"
      />
    </SModal>
  </VForm>
</template>

<script>
import ClassbookNoticeUsers from 'src/components/classbook/notices/ClassbookNoticeUsers.vue'
import SduiUserListItem from 'src/components/user/SduiUserListItem.vue'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import { User } from 'src/store/models/index.js'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import SduiDateTimeSelect from 'src/components/miscellaneous/ui/SduiDateTimeSelect.vue'
import SduiGroupSelect from 'src/components/miscellaneous/ui/SduiGroupSelect.vue'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import Classbooks from 'src/api/classbooks.js'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'ClassbookNotice',
  components: {
    SduiGroupSelect,
    SduiLoader,
    ClassbookNoticeUsers,
    SduiDateTimeSelect,
    SduiUserListItem,
    SModalPortalTo,
  },

  props: {
    notice: {
      type: Object,
      default: () => ({}),
    },

    entry: {
      type: Object,
      default: () => ({}),
    },

    externalUsers: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    localNotice: {
      noticeMessage: '',
      category: null,
      date: '',
      localUsers: [],
    },

    modals: {
      datetime: false,
      users: false,
    },

    categories: [],
    selectedUsers: [],
  }),

  computed: {
    calculatedCategories() {
      return this.categories.map((s) => ({
        uuid: s.uuid,
        name: s.name,
      }))
    },

    editMode() {
      return this.notice?.id
    },

    classbookId() {
      return this.entry?.classbook_id || this.notice?.classbook_id
    },

    grades() {
      // grades should be excludes when users are selected
      if (this.users.length) return []
      return this.entry?.grades
    },

    gradeIds() {
      return this.grades.map((g) => g.id)
    },

    users() {
      return this.localNotice.localUsers
    },

    userIds() {
      return this.users.map((u) => u.id)
    },
  },

  emits: ['submit', 'close'],

  methods: {
    openUsersModal() {
      this.selectedUsers = this.localNotice.localUsers
      this.handleNoticeUserSelectModalDisplay()
    },

    saveUsers() {
      this.localNotice.localUsers = this.selectedUsers
      this.handleNoticeUserSelectModalDisplay(true)
    },

    removeUser(id) {
      this.localNotice.localUsers = this.localNotice.localUsers.filter((u) => u.id !== id)
    },

    createNotice: waitFor('creating-notice', async function () {
      const data = {
        entry_id: this.notice?.entry_id || this.entry?.id,
        grades: this.gradeIds,
        users: this.userIds,
        category_uuid: this.localNotice.category,
        occurred_at: this.$dateFormat.timestamp(this.localNotice.date),
        message: this.localNotice.noticeMessage,
      }

      try {
        const res = this.editMode
          ? await Classbooks.putNoticesById(this.classbookId, this.notice.id, data)
          : await Classbooks.postNotices(this.classbookId, data)
        clyTracking.trackClassbook('notice').end.save({ isEditMode: this.editMode, trackGlobal: { feature: 'classbook' } })
        this.$store.commit('timetable/SET_NOTICE_CREATION', {})
        this.$emit('submit', res.data)
        this.$emit('close')
      } catch (e) {
        clyTracking.trackClassbook('notice', 'FAILED', e.message).end.save({ isEditMode: this.editMode })
        throw e
      }
    }),

    fetchCategories: waitFor('fetching-categories', async function () {
      const res = await Classbooks.getNoticesCategories(this.classbookId)
      this.categories = res.data
    }),

    async initialize() {
      await this.fetchCategories()

      const noticeCreation = await this.$store.getters['timetable/NOTICE_CREATION']
      if (Object.keys(noticeCreation).length) return this.restoreStateIfPossible(noticeCreation)

      this.localNotice = {
        date: this.notice?.occurred_at || this.$dateFormat.timestamp(this.entry.begins_at),
        noticeMessage: this.notice?.message,
        category: this.notice?.category_uuid || this.categories[0].uuid,
        localUsers: (this.notice?.users || []).map((user) => new User(user)),
      }
      await this.initVeeValidateField()

      if (!this.externalUsers.length) return
      this.localNotice = {
        ...this.localNotice,
        localUsers: (this.externalUsers || []).map((user) => new User(user)),
        date: this.entry.begins_at,
      }

      await this.initVeeValidateField()
    },

    async handleNoticeUserSelectModalDisplay(close = false) {
      // method is being called when modal is closed or hidden. The hidden event also fires when closed so prevent
      // duplicate navigation here in this case
      const newQueryParam = {
        ...this.$route.query,
        showNoticeUsersModal: close ? undefined : 'true',
        noticeId: this.notice.id && !close ? this.notice.id : undefined,
      }
      if (this.$compare(this.$route.query, newQueryParam)) return

      // store current state
      await this.$store.commit('timetable/SET_NOTICE_CREATION', close ? {} : this.localNotice)

      // Set queryParam "showNoticeUsersModal=true" to show the user select modal (see also watcher). If an existing
      // notice is being edited, also set queryParam "noticeId=this.notice.id".
      // The queryParam is being checked if the user clicks the DC link (course membership) and returns back.
      // This way we can open the modal again on return to this feature (router.go(-1))
      this.$router.replace({
        params: { ...this.$route.params },
        query: newQueryParam,
      })

      if (!close) return
      this.selectedUsers = []
      this.modals.users = false
    },

    async restoreStateIfPossible(noticeCreation) {
      // restore state for the notice object
      this.localNotice = this.$clone(noticeCreation)
      // also restore state of selectedUser so the user select modal has the same users selected as before
      this.selectedUsers = noticeCreation.localUsers
      await this.initVeeValidateField()
    },

    async initVeeValidateField() {
      if (!this.localNotice.noticeMessage) return
      await this.$nextTick()
      this.$refs.form.setFieldValue('notice_message', this.localNotice.noticeMessage)
    },
  },

  watch: {
    // if user comes back from DC courseMembership we need to open the notice USER SELECTION modal again
    '$route.query.showNoticeUsersModal': {
      handler: function (showModal) {
        if (!showModal || this.modals.users) return
        this.modals.users = !!showModal
      },

      immediate: true,
    },
  },

  async mounted() {
    await this.initialize()
  },
}
</script>

<style scoped lang="scss">
.smaller-btn {
  font-size: pxtorem(14) !important;
  line-height: pxtorem(14);
}

.fixed-bottom.position-absolute {
  box-shadow: 0 0 8px rgba($color-black-rgb, 0.16);
  padding: 12px !important;
  border-bottom-left-radius: 1.0625rem;
  border-bottom-right-radius: 1.0625rem;
}

.notice-targets {
  span:last-child i {
    display: none;
  }
}
</style>
